import { toMicro, type Condition, type ItemType } from '@brickloop/core/schemas';
import type { Color } from '../api';

/** Parts and unsorted lots come in colours; everything else has the single colour 0, "(Not Applicable)". */
export const usesColor = (t: ItemType): boolean => t === 'PART' || t === 'UNSORTED_LOT';

/** Finds a colour from what was typed: its name (any case) or, for a bare number, its BrickLink id. */
export function resolveColor(text: string, type: ItemType, colors: Color[] | undefined): Color | null {
  if (!colors) return null;
  if (!usesColor(type)) return colors.find((c) => c.id === 0) ?? null;
  const t = text.trim().toLowerCase();
  if (!t) return null;
  return colors.find((c) => c.name.toLowerCase() === t) ?? (/^\d+$/.test(t) ? (colors.find((c) => c.id === Number(t)) ?? null) : null);
}

export interface OrderLineDraft {
  key: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorText: string;
  condition: Condition;
  qty: string;
  unitPrice: string;
}

export interface OrderDraft {
  purchasedOn: string;
  sellerName: string;
  description: string;
  currency: string;
  exchangeRate: string;
  shippingCost: string;
  insuranceCost: string;
  otherCharges: string;
  taxAmount: string;
  discountAmount: string;
  vatScheme: 'none' | 'standard' | 'margin';
  notes: string;
  lines: OrderLineDraft[];
}

let nextKey = 1;
export const blankLine = (condition: Condition = 'U'): OrderLineDraft => ({
  key: nextKey++,
  itemType: 'PART',
  itemNo: '',
  itemName: '',
  colorText: '',
  condition,
  qty: '',
  unitPrice: '',
});

export const blankOrder = (defaultCurrency: string, today = new Date().toISOString().slice(0, 10)): OrderDraft => ({
  purchasedOn: today,
  sellerName: '',
  description: '',
  currency: defaultCurrency,
  exchangeRate: '',
  shippingCost: '',
  insuranceCost: '',
  otherCharges: '',
  taxAmount: '',
  discountAmount: '',
  vatScheme: 'none',
  notes: '',
  lines: [blankLine()],
});

/** A line the person has touched. An untouched blank row is ignored, so a spare row never blocks sending. */
const isFilled = (l: OrderLineDraft) => !!(l.itemNo.trim() || l.qty.trim() || l.unitPrice.trim() || l.colorText.trim());

const isMoney = (v: string) => toMicro(v) !== null && toMicro(v)! >= 0;
const blankToZero = (v: string) => (v.trim() === '' ? '0' : v.trim());

export type OrderBody = {
  purchasedOn: string;
  description: string;
  sellerName: string | null;
  currency: string;
  exchangeRate: number | null;
  vatScheme: OrderDraft['vatScheme'];
  notes: string | null;
  shippingCost: string;
  insuranceCost: string;
  otherCharges: string;
  taxAmount: string;
  discountAmount: string;
  lines: { itemType: ItemType; itemNo: string; itemName?: string; colorId: number; condition: Condition; qty: number; unitPrice: string }[];
};

/**
 * Checks the form the way the API will, so the person is told what is wrong beside the row rather than after a round trip,
 * and builds the body for POST /api/v1/purchase-orders. Returns every problem found, not just the first.
 */
export function buildOrderBody(
  d: OrderDraft,
  colors: Color[] | undefined,
  defaultCurrency: string,
): { ok: true; body: OrderBody } | { ok: false; problems: string[] } {
  const problems: string[] = [];
  const currency = d.currency.trim().toUpperCase();
  if (!d.purchasedOn) problems.push('Choose the date the order was placed.');
  if (!/^[A-Z]{3}$/.test(currency)) problems.push('The currency is a three-letter code such as GBP or EUR.');

  let exchangeRate: number | null = null;
  if (currency !== defaultCurrency.toUpperCase()) {
    const r = Number(d.exchangeRate);
    if (d.exchangeRate.trim() === '') {
      // Not needed to place the order, only to receive it, so an empty rate is allowed.
      exchangeRate = null;
    } else if (!Number.isFinite(r) || r <= 0) problems.push(`The exchange rate is how many ${defaultCurrency.toUpperCase()} one ${currency || 'unit'} is worth, a number above zero.`);
    else exchangeRate = r;
  }

  const charges = [
    ['Postage', d.shippingCost],
    ['Insurance', d.insuranceCost],
    ['Other charges', d.otherCharges],
    ['VAT', d.taxAmount],
    ['Discount', d.discountAmount],
  ] as const;
  for (const [label, v] of charges) if (v.trim() !== '' && !isMoney(v)) problems.push(`${label} is not an amount.`);

  const filled = d.lines.map((l, i) => ({ l, row: i + 1 })).filter(({ l }) => isFilled(l));
  if (filled.length === 0) problems.push('Add at least one line.');
  const lines: OrderBody['lines'] = [];
  for (const { l, row } of filled) {
    const here: string[] = [];
    if (!l.itemNo.trim()) here.push('give the item number');
    const color = resolveColor(l.colorText, l.itemType, colors);
    if (!color) here.push(usesColor(l.itemType) ? (l.colorText.trim() ? `"${l.colorText.trim()}" is not a colour in the catalogue` : 'choose a colour') : 'colour 0 is missing from the catalogue');
    const qty = Number(l.qty);
    if (!Number.isInteger(qty) || qty < 1) here.push('quantity is a whole number of at least 1');
    if (l.unitPrice.trim() === '' || !isMoney(l.unitPrice)) here.push('give the price of one piece');
    if (here.length > 0) {
      problems.push(`Line ${row}: ${here.join(', ')}.`);
      continue;
    }
    lines.push({
      itemType: l.itemType,
      itemNo: l.itemNo.trim(),
      ...(l.itemName.trim() && { itemName: l.itemName.trim() }),
      colorId: color!.id,
      condition: l.condition,
      qty,
      unitPrice: l.unitPrice.trim(),
    });
  }
  if (problems.length > 0) return { ok: false, problems };

  return {
    ok: true,
    body: {
      purchasedOn: d.purchasedOn,
      description: d.description.trim(),
      sellerName: d.sellerName.trim() || null,
      currency,
      exchangeRate,
      vatScheme: d.vatScheme,
      notes: d.notes.trim() || null,
      shippingCost: blankToZero(d.shippingCost),
      insuranceCost: blankToZero(d.insuranceCost),
      otherCharges: blankToZero(d.otherCharges),
      taxAmount: blankToZero(d.taxAmount),
      discountAmount: blankToZero(d.discountAmount),
      lines,
    },
  };
}

/** What the lines come to, in micro units, for the running total shown under the editor. Rows that are not valid yet count as nothing. */
export function linesTotalMicro(lines: OrderLineDraft[]): number {
  let total = 0;
  for (const l of lines) {
    const qty = Number(l.qty);
    const price = toMicro(l.unitPrice);
    if (Number.isInteger(qty) && qty > 0 && price !== null && price >= 0) total += qty * price;
  }
  return total;
}
